import { useMemo } from "react";
import Constants from "config/Constants";
import useCanDo from "hooks/useCanDo";
import useHyroxTier from "hooks/useHyroxTier";
import DashboardIcon from "components/Icons/DashboardIcon";
import ActivityIcon from "components/Icons/ActivityIcon";
import AthletesIcon from "components/Icons/AthletesIcon";
import LineChartIcon from "components/Icons/LineChartIcon";
import GroupsIcon from "components/Icons/GroupsIcon";
import LeaderboardsIcon from "components/Icons/LeaderboardsIcon";
import ExercisesIcon from "components/Icons/ExercisesIcon";
import WorkoutsIcon from "components/Icons/WorkoutsIcon";
import ExportIcon from "components/Icons/ExportIcon";
import ProgramsIcon from "components/Icons/ProgramsIcon";
import CalendarIcon from "components/Icons/CalendarIcon";
import type { CanDoActions } from "types/CanDoActions";
import type { UpsellFeature } from "types/upsell";
import useDashboardVelocityHotspot from "hooks/useDashboardVelocityHotspot";
import useAthleteVelocityHotspot from "hooks/useAthleteVelocityHotspot";

export interface SidebarRoute {
  route: string;
  title: string;
  /**
   * Matches `SidebarRouteItem`'s own `icon` prop. Deliberately wider than
   * `FunctionComponent<SvgIconProps>` — several icons in `components/Icons`
   * narrow `fontSize` to their own supported sizes, so the stricter type only
   * assigns via a cast.
   */
  icon: React.ElementType;
  /**
   * Renders the item as inert (dimmed, non-interactive). Used by the HYROX
   * tiers, where a locked route is visible but must not be clickable.
   */
  locked?: boolean;
  /**
   * Renders the item as a locked *upsell* entry: still a link, but dimmed with a
   * lock icon and a marketing tooltip on hover. Set by `useUpsellSidebarRoutes`.
   *
   * Distinct from `locked` on purpose — HYROX locked items must stay inert.
   */
  upsell?: SidebarUpsell;
  /** Shows an onboarding hotspot dot on the icon. */
  hotspot?: boolean;
}

export interface SidebarUpsell {
  /** Translation key for the marketing tooltip shown on hover. */
  tooltip: string;
  feature: UpsellFeature;
}

export interface SidebarRouteGroup {
  title: string;
  items: SidebarRoute[];
}

const HYROX_T0_ROUTES = ["/leaderboards", "/dashboards", "/charting", "/groups"];
const HYROX_T1_ROUTES = ["/leaderboards", "/dashboards", "/charting", "/groups", "/calendar", "/programs"];

const ROUTE_TO_ACTION: Record<string, CanDoActions> = {
  "/leaderboards": "leaderboards.any",
  "/dashboards": "dashboard.any",
  "/charting": "charting.any",
  "/groups": "group.any",
  "/programs": "programs.any",
  "/calendar": "calendar.any",
};

function useSidebarRouteList(): SidebarRouteGroup[] {
  const canDo = useCanDo();
  const { isHyrox, isHyroxT0, isHyroxT1 } = useHyroxTier();
  const dashboardHotspot = useDashboardVelocityHotspot();
  const athleteHotspot = useAthleteVelocityHotspot();

  return useMemo(() => {
    const routing = [
      {
        title: "global.intent_label",
        items: [
          {
            route: "/live",
            title: "pages.live_title",
            icon: ActivityIcon,
          },
          (canDo("leaderboards.any") || isHyroxT0) && {
            route: Constants.routes.leaderboards.route,
            title: Constants.routes.leaderboards.titleKey,
            icon: LeaderboardsIcon,
          },
        ],
      },
      {
        title: "global.analyze_label",
        items: [
          (canDo("dashboard.any") || isHyroxT0) && {
            route: "/dashboards",
            title: "pages.dashboard_title",
            icon: DashboardIcon,
            hotspot: dashboardHotspot.open,
          },
          (canDo("charting.any") || isHyroxT0) && {
            route: "/charting",
            title: "pages.charting_title",
            icon: LineChartIcon,
          },
        ],
      },
      {
        title: "global.manage_label",
        items: [
          {
            route: Constants.routes.athletes.route,
            title: Constants.routes.athletes.titleKey,
            icon: AthletesIcon,
            hotspot: athleteHotspot.open,
          },
          (canDo("group.any") || isHyroxT0 || isHyroxT1) && {
            route: Constants.routes.groups.route,
            title: Constants.routes.groups.titleKey,
            icon: GroupsIcon,
          },
          (canDo("calendar.any") || isHyroxT1) && {
            route: Constants.routes.calendar.route,
            title: Constants.routes.calendar.titleKey,
            icon: CalendarIcon,
          },
        ],
      },
      {
        title: "global.libraries_label",
        items: [
          canDo("library.any") && {
            route: Constants.routes.exerciseLibrary.route,
            title: Constants.routes.exerciseLibrary.titleKey,
            icon: ExercisesIcon,
          },
          canDo("workout.any") && {
            route: "/workouts",
            title: "pages.workouts_title",
            icon: WorkoutsIcon,
          },
          (canDo("programs.any") || isHyroxT1) && {
            route: "/programs",
            title: "pages.programs_title",
            icon: ProgramsIcon,
          },
        ],
      },
      {
        title: "global.generate_label",
        items: [
          canDo("export.any") && {
            route: Constants.routes.exportData.route,
            title: Constants.routes.exportData.titleKey,
            icon: ExportIcon,
          },
        ],
      },
    ];

    const filtered: SidebarRouteGroup[] = [];

    routing.forEach((group) => {
      const routes = group.items.filter(Boolean) as SidebarRoute[];

      const hyroxRoutes = isHyroxT0 ? HYROX_T0_ROUTES : isHyroxT1 ? HYROX_T1_ROUTES : [];

      const processedRoutes = isHyrox
        ? routes
            .filter((route) => {
              const action = ROUTE_TO_ACTION[route.route];
              if (!action) return true;
              return canDo(action) || hyroxRoutes.includes(route.route);
            })
            .map((route) => {
              const action = ROUTE_TO_ACTION[route.route];
              if (action && hyroxRoutes.includes(route.route) && !canDo(action)) {
                return { ...route, locked: true };
              }
              return route;
            })
        : routes;

      if (processedRoutes.length > 0) {
        filtered.push({ title: group.title, items: processedRoutes });
      }
    });

    return filtered;
  }, [athleteHotspot.open, canDo, dashboardHotspot.open, isHyrox, isHyroxT0, isHyroxT1]);
}

export default useSidebarRouteList;
